import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BadgeMedal } from "@/components/BadgeMedal";
import AccessBanner from "@/components/AccessBanner";
import LimitedAccessTour from "@/components/LimitedAccessTour";
import {
  BookOpen, Sparkles, Trophy, Award, ChevronRight, Flame, Target, CheckCircle2,
  Clock, AlertTriangle, Brain, GraduationCap, Crown, ArrowRight, Lock, TrendingUp,
  ListChecks, Activity, Users,
} from "lucide-react";
import StreakRescueBanner from "@/components/StreakRescueBanner";

export default function MobileStudentHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [smart, setSmart] = useState(null);
  const [course, setCourse] = useState(null);
  const [lb, setLb] = useState(null);
  const [badges, setBadges] = useState(null);

  useEffect(() => {
    api.get("/student/dashboard").then((r) => setStats(r.data));
    api.get("/student/smart-review/summary").then((r) => setSmart(r.data)).catch(() => {});
    // Use /student/my-enrollments (real enrollments only) instead of /courses
    // (which falls back to "all active courses" when the student has none —
    // making a deleted student appear enrolled in the first available course).
    // Prefer the primary (selected_course_id) enrolled course; fall back to
    // the first non-suspended one; otherwise leave null so the "Enroll Now"
    // empty state renders.
    api.get("/student/my-enrollments").then((r) => {
      const enrolled = (r.data?.enrolled || []).filter((c) => !c.suspended);
      const primary = enrolled.find((c) => c.is_primary) || enrolled[0] || null;
      setCourse(primary);
    }).catch(() => {});
    api.get("/student/leaderboard?metric=points").then((r) => setLb(r.data)).catch(() => {});
    api.get("/student/badges").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  const firstName = (user?.full_name || "").split(" ")[0] || "there";
  const isPending = user?.status === "pending";

  if (!stats) {
    return (
      <div className="space-y-3" data-testid="mobile-loading">
        <div className="h-28 rounded-3xl bg-gradient-to-br from-indigo-200 to-purple-200 animate-pulse" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      </div>
    );
  }

  // De-dupe recent activity by hour bucket + correctness so user doesn't see 5 identical rows
  const recentRaw = stats.recent || [];
  const seen = new Set();
  const recent = [];
  for (const a of recentRaw) {
    const key = `${(a.answered_at || "").slice(0, 13)}-${a.is_correct ? "ok" : "no"}-${a.module_id || ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      recent.push(a);
    }
    if (recent.length >= 4) break;
  }

  return (
    <div className="space-y-6" data-testid="mobile-student-home">
      <LimitedAccessTour />
      <AccessBanner compact={true} />
      {/* HERO */}
      <section className="relative overflow-hidden rounded-3xl text-white px-5 py-6 shadow-lg shadow-indigo-300/30"
               style={{ background: "linear-gradient(135deg,#4F46E5 0%,#7C3AED 50%,#A855F7 100%)" }}
               data-testid="mobile-hero">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/15 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-8 w-44 h-44 rounded-full bg-fuchsia-300/20 blur-2xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">Welcome back</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Hello, {firstName} <span className="inline-block">👋</span></h1>
            <p className="text-sm text-white/85 mt-1">Ready to master your review today?</p>
            <p className="text-xs text-white/60 mt-2 leading-snug">Small daily progress leads to board exam success.</p>
          </div>
          {stats.streak && (
            <div className="text-center shrink-0 rounded-2xl bg-white/15 backdrop-blur border border-white/20 px-3 py-2.5 min-w-[68px]"
                 data-testid="hero-streak-badge">
              <Flame className={`w-5 h-5 mx-auto ${stats.streak.active_today ? "text-orange-200" : "text-white/50"}`} strokeWidth={2} />
              <div className="font-display text-xl font-bold leading-none mt-1">{stats.streak.current_streak}</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-white/70 mt-0.5">Day{stats.streak.current_streak === 1 ? "" : "s"}</div>
            </div>
          )}
        </div>
      </section>

      {/* STATS GRID */}
      <section className="grid grid-cols-2 gap-3" data-testid="mobile-summary-chips">
        <StatTile icon={Target} label="Accuracy" value={`${stats.score_percentage}%`}
                  helper={stats.score_percentage >= 80 ? "Great progress" : stats.score_percentage >= 60 ? "Improving" : "Keep practicing"}
                  tint="blue" />
        <StatTile icon={Clock} label="Due Today" value={smart?.due_today ?? 0}
                  helper={(smart?.due_today ?? 0) > 0 ? "Review now" : "All caught up"}
                  tint="purple" />
        <StatTile icon={CheckCircle2} label="Answered" value={stats.total_answered}
                  helper={`${stats.correct} correct`}
                  tint="emerald" />
        <StatTile icon={Trophy} label="Rank"
                  value={lb?.your_rank ? `#${lb.your_rank}` : "—"}
                  helper={lb?.total_qualified ? `of ${lb.total_qualified}` : "Earn points"}
                  tint="amber" />
      </section>

      {/* STREAK RESCUE — only renders when streak >=3 and not active today */}
      {stats.streak && (
        <StreakRescueBanner
          stats={stats}
          onFrozen={() => api.get("/student/dashboard").then((r) => setStats(r.data))}
        />
      )}

      {/* CONTINUE LEARNING */}
      <section data-testid="mobile-section-course">
        <SectionHeader title="Continue Learning" />
        <CourseCard course={course} isPending={isPending} progress={stats.score_percentage} />
      </section>

      {/* SMART REVIEW FEATURE */}
      <section data-testid="mobile-section-smart">
        <SmartReviewCard smart={smart} />
      </section>

      {/* QUICK ACTIONS */}
      <section data-testid="mobile-quick-actions">
        <SectionHeader title="Quick Actions" />
        <div className="grid grid-cols-2 gap-3">
          <QuickAction onClick={() => nav("/student/courses")}
                       icon={BookOpen} label="Review Mode" tint="blue"
                       testId="qa-review-mode" />
          <QuickAction onClick={() => nav("/student/courses")}
                       icon={ListChecks} label="Exam Mode" tint="rose"
                       testId="qa-exam-mode" />
          <QuickAction onClick={() => nav("/student/smart-review")}
                       icon={Brain} label="Smart Review" tint="violet"
                       testId="qa-smart-review" />
          <QuickAction onClick={() => nav("/student/analytics")}
                       icon={TrendingUp} label="My Analytics" tint="emerald"
                       testId="qa-analytics" />
          <QuickAction onClick={() => nav("/student/enrollments")}
                       icon={GraduationCap} label="Enrollments" tint="indigo"
                       testId="qa-enrollments" />
          <QuickAction onClick={() => nav("/student/progress-card")}
                       icon={Award} label="Progress Card" tint="amber"
                       testId="qa-progress-card" />
          <QuickAction onClick={() => nav("/student/mock-exam")}
                       icon={Trophy} label="Mock Exam" tint="orange"
                       testId="qa-mock-exam" />
          <QuickAction onClick={() => nav("/student/friends")}
                       icon={Users} label="Friends & QR" tint="pink"
                       testId="qa-friends" />
        </div>
      </section>

      {/* LEADERBOARD PREVIEW */}
      <section data-testid="mobile-section-leaderboard">
        <SectionHeader title="Leaderboard" action="View" to="/student/leaderboard" testId="lb-view-all" />
        <LeaderboardPreview lb={lb} />
      </section>

      {/* BADGES PREVIEW */}
      <section data-testid="mobile-section-badges">
        <SectionHeader title="Badges" action="View all" to="/student/badges" testId="badges-view-all" />
        <BadgesPreview badges={badges} />
      </section>

      {/* RECENT ACTIVITY */}
      <section data-testid="mobile-recent-activity">
        <SectionHeader title="Recent Activity"
                       action={recent.length > 0 ? "View all" : null}
                       to="/student/progress-card"
                       testId="activity-view-all" />
        <Card className="border-0 rounded-2xl shadow-sm bg-white overflow-hidden">
          {recent.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <Activity className="w-7 h-7 mx-auto text-slate-300" strokeWidth={1.5} />
              <div className="font-semibold text-sm mt-2 text-slate-800">No activity yet</div>
              <p className="text-xs text-slate-500 mt-1">Start your first review session to see your progress here.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((a, i) => (
                <div key={a.id || a.answered_at || `activity-${i}`} className="px-4 py-3 flex items-center gap-3" data-testid={`mobile-activity-${i}`}>
                  <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${a.is_correct ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                    {a.is_correct ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">
                      {a.is_correct ? "Correct Answer" : "Missed a question"}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      You answered {a.is_correct ? "correctly" : "incorrectly"} · {(a.answered_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${a.is_correct ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {a.selected_answer}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function SectionHeader({ title, action, to, testId }) {
  return (
    <div className="flex items-center justify-between mb-2.5 px-0.5">
      <h2 className="font-display font-bold text-base text-slate-900">{title}</h2>
      {action && (
        <Link to={to} className="text-xs font-semibold text-blue-700 inline-flex items-center gap-0.5" data-testid={testId}>
          {action} <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, helper, tint = "blue" }) {
  const map = {
    blue: { bg: "bg-blue-100", icon: "text-blue-600" },
    purple: { bg: "bg-violet-100", icon: "text-violet-600" },
    emerald: { bg: "bg-emerald-100", icon: "text-emerald-600" },
    amber: { bg: "bg-amber-100", icon: "text-amber-600" },
    orange: { bg: "bg-orange-100", icon: "text-orange-600" },
  };
  const c = map[tint] || map.blue;
  return (
    <Card className="p-3.5 border-0 rounded-2xl shadow-sm bg-white" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className={`h-9 w-9 rounded-xl grid place-items-center ${c.bg} ${c.icon}`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">{label}</div>
        <div className="font-display text-xl font-extrabold text-slate-900 leading-none mt-1">{value}</div>
        {helper && <div className="text-[10px] text-slate-400 mt-1.5">{helper}</div>}
      </div>
    </Card>
  );
}

function CourseCard({ course, isPending, progress }) {
  if (isPending || !course) {
    // No enrollment (never enrolled, or admin deleted it) OR still pending.
    // Show a clear CTA to enroll — DO NOT show a stale "current course"
    // reference so the student can never think a deleted course is still theirs.
    return (
      <Card className="p-5 border-0 rounded-2xl shadow-sm bg-white" data-testid="mobile-card-course-empty">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-base">
              {isPending ? "Enrollment Pending" : "No Course Enrolled"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {isPending
                ? "Your enrollment is still pending admin approval."
                : "You are not currently enrolled in any course."}
            </p>
          </div>
        </div>
        {!isPending && (
          <Link
            to="/student/enrollments"
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#1E3A8A] text-white text-sm font-bold active:scale-[0.98] transition-transform"
            data-testid="mobile-card-course-enroll-cta"
          >
            Enroll in a Course <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </Card>
    );
  }
  const pct = Math.max(0, Math.min(100, progress || 0));
  return (
    <Link to={`/student/courses/${course.id}/subjects`} className="block" data-testid="mobile-card-course">
      <Card className="p-0 border-0 rounded-2xl shadow-sm overflow-hidden bg-white active:scale-[0.99] transition-transform">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white grid place-items-center shrink-0 shadow-md shadow-indigo-200">
              <GraduationCap className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600">Current Course</div>
              <h3 className="font-display font-bold text-base mt-0.5 leading-tight line-clamp-1">{course.course_name}</h3>
              {course.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{course.description}</p>}
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 mt-1 shrink-0" />
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <span className="text-slate-500 font-medium">Progress</span>
              <span className="font-bold text-slate-800">{pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all"
                   style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
        <div className="px-5 pb-4">
          <div className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#1E3A8A] text-white text-sm font-bold">
            Continue Course <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </Card>
    </Link>
  );
}

function SmartReviewCard({ smart }) {
  const hasData = smart?.has_data;
  return (
    <Link to="/student/smart-review" className="block" data-testid="mobile-card-smart-review">
      <Card className="border-0 rounded-2xl shadow-lg shadow-violet-200/40 overflow-hidden p-0 active:scale-[0.99] transition-transform">
        <div className="relative px-5 py-5 text-white"
             style={{ background: "linear-gradient(135deg,#6D28D9 0%,#7C3AED 50%,#A855F7 100%)" }}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/15 blur-2xl pointer-events-none" />
          <div className="relative flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl grid place-items-center bg-white/20 backdrop-blur border border-white/20 shrink-0">
              <Sparkles className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/75">Smart Review</div>
              <h3 className="font-display font-bold text-lg mt-0.5 leading-tight">AI-powered review</h3>
              <p className="text-xs text-white/80 mt-0.5">
                {hasData ? `You have ${smart.due_today} question${smart.due_today === 1 ? "" : "s"} due today.` : "Answer more questions to unlock personalized recommendations."}
              </p>
              {hasData && (smart.weakest_subject || smart.accuracy != null) && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {smart.weakest_subject && (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-white/15 backdrop-blur border border-white/15">
                      Weakest: {smart.weakest_subject.name} ({smart.weakest_subject.accuracy}%)
                    </span>
                  )}
                  {smart.accuracy != null && (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-white/15 backdrop-blur border border-white/15">
                      Accuracy {smart.accuracy}%
                    </span>
                  )}
                </div>
              )}
              <div className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white text-violet-700 text-sm font-bold shadow-sm">
                Start Smart Review <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function QuickAction({ icon: Icon, label, tint, onClick, testId }) {
  const map = {
    blue: { bg: "from-blue-500 to-blue-600", shadow: "shadow-blue-200" },
    rose: { bg: "from-rose-500 to-rose-600", shadow: "shadow-rose-200" },
    violet: { bg: "from-violet-500 to-violet-600", shadow: "shadow-violet-200" },
    emerald: { bg: "from-emerald-500 to-emerald-600", shadow: "shadow-emerald-200" },
    indigo: { bg: "from-indigo-500 to-indigo-600", shadow: "shadow-indigo-200" },
    amber: { bg: "from-amber-500 to-amber-600", shadow: "shadow-amber-200" },
    orange: { bg: "from-orange-500 to-orange-600", shadow: "shadow-orange-200" },
    pink: { bg: "from-pink-500 to-pink-600", shadow: "shadow-pink-200" },
  };
  const c = map[tint] || map.blue;
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className="text-left w-full p-3.5 rounded-2xl bg-white shadow-sm border-0 active:scale-[0.98] transition-transform">
      <div className={`h-10 w-10 rounded-xl grid place-items-center text-white bg-gradient-to-br ${c.bg} shadow-md ${c.shadow}`}>
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="font-display font-bold text-sm text-slate-900 mt-3">{label}</div>
      <div className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-0.5">Open <ChevronRight className="w-3 h-3" /></div>
    </button>
  );
}

function LeaderboardPreview({ lb }) {
  if (!lb || lb.entries.length === 0) {
    return (
      <Card className="border-0 rounded-2xl shadow-sm bg-white p-4 flex items-center gap-3" data-testid="mobile-card-leaderboard">
        <div className="h-11 w-11 rounded-2xl grid place-items-center bg-amber-100 text-amber-600">
          <Trophy className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm text-slate-800">No rankings yet</div>
          <div className="text-xs text-slate-500 mt-0.5">Earn points to enter the leaderboard.</div>
        </div>
      </Card>
    );
  }
  const top = lb.entries[0];
  return (
    <Link to="/student/leaderboard" className="block" data-testid="mobile-card-leaderboard">
      <Card className="border-0 rounded-2xl shadow-sm bg-white p-4 flex items-center gap-3 active:scale-[0.99] transition-transform">
        <div className="h-12 w-12 rounded-2xl grid place-items-center text-white shrink-0 shadow-md shadow-amber-200"
             style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}>
          <Crown className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-extrabold text-lg text-slate-900 leading-none">
              {lb.your_rank ? `You are #${lb.your_rank}` : "Climb the board"}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate">
            {lb.your_rank ? `of ${lb.total_qualified} students` : "Earn points to rank"}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5 truncate">
            Top: <span className="font-semibold text-slate-800">{top.display_name}</span> · {top.points ?? 0} pts
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
      </Card>
    </Link>
  );
}

function BadgesPreview({ badges }) {
  if (!badges) {
    return <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />;
  }
  const items = badges.badges.slice(0, 6);
  return (
    <Link to="/student/badges" className="block" data-testid="mobile-card-badges">
      <Card className="border-0 rounded-2xl shadow-sm bg-white p-4 active:scale-[0.99] transition-transform">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-extrabold text-base text-slate-900">{badges.earned_count} of {badges.total_count} unlocked</div>
            <div className="text-xs text-slate-500 mt-0.5">Keep going to earn more achievements</div>
          </div>
          <div className="h-11 w-11 rounded-2xl grid place-items-center text-white shrink-0 shadow-md shadow-emerald-200"
               style={{ background: "linear-gradient(135deg,#10B981,#0D9488)" }}>
            <Award className="w-5 h-5" strokeWidth={1.75} />
          </div>
        </div>
        <div className="flex gap-2.5 overflow-x-auto -mx-1 px-1 pb-1 no-scrollbar">
          {items.map((b) => (
            <div key={b.id} className="shrink-0">
              <BadgeMedal badge={b} size="sm" />
            </div>
          ))}
        </div>
      </Card>
    </Link>
  );
}
